import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, BookOpen, FileText, Video, Scale, HelpCircle,
  Layers, Sparkles, ChevronLeft,
  ChevronRight, Loader2
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { fetchBlogPosts, type BlogPost } from '@/lib/cms';
import BreadcrumbJsonLd from '@/components/BreadcrumbJsonLd';

const GRADIENT_PALETTES = [
  'from-[#635bff] to-[#00d4ff]',
  'from-[#00d4ff] to-[#00ff88]',
  'from-[#a855f7] to-[#ec4899]',
  'from-[#ff6b6b] to-[#ffa500]',
  'from-[#635bff] to-[#a855f7]',
  'from-[#00d4ff] to-[#635bff]',
];

function getPostGradient(index: number): string {
  return GRADIENT_PALETTES[index % GRADIENT_PALETTES.length];
}

function estimateReadTime(content: string): string {
  const text = content.replace(/<[^>]+>/g, '');
  const words = text.split(/\s+/).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
}

const resourceSections = [
  {
    id: 'templates',
    title: 'HTML5 Ad Templates',
    description: 'Browse our collection of professionally designed HTML5 ad templates. Customize and deploy in minutes.',
    icon: Layers,
    href: '/resources/templates',
    color: 'from-[#635bff] to-[#00d4ff]',
    stats: '50+ Templates',
    external: false,
  },
  {
    id: 'help',
    title: 'Help Center',
    description: 'Search our knowledge base or browse categories to find answers to your questions.',
    icon: HelpCircle,
    href: 'https://help.adventive.com',
    color: 'from-[#ff6b6b] to-[#ffa500]',
    stats: '100+ Articles',
    external: true,
  },
  {
    id: 'legal',
    title: 'Legal Documents',
    description: 'Review our terms, policies, and legal agreements that govern your use of Adventive services.',
    icon: Scale,
    href: '/resources/legal',
    color: 'from-[#a855f7] to-[#ec4899]',
    stats: '6 Documents',
    external: false,
  },
];

const Resources: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [blogLoading, setBlogLoading] = useState(true);
  const [blogError, setBlogError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadBlogPosts() {
      try {
        setBlogLoading(true);
        setBlogError(null);
        const { posts } = await fetchBlogPosts(100);
        if (cancelled) return;
        setBlogPosts(posts);
      } catch (err) {
        if (cancelled) return;
        setBlogError(err instanceof Error ? err.message : 'Failed to load blog posts');
      } finally {
        if (!cancelled) setBlogLoading(false);
      }
    }

    loadBlogPosts();
    return () => { cancelled = true; };
  }, []);

  const totalPages = Math.ceil(blogPosts.length / postsPerPage);
  const paginatedPosts = blogPosts.slice(
    (currentPage - 1) * postsPerPage,
    currentPage * postsPerPage
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0f]">
      <BreadcrumbJsonLd crumbs={[
        { name: 'Home', item: '/' },
        { name: 'Resources' },
      ]} />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#635bff]/20 via-transparent to-[#00d4ff]/10" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#635bff]/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#00d4ff]/15 rounded-full blur-3xl animate-float-delayed" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-4xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-[#635bff] bg-[#635bff]/10 border border-[#635bff]/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4" />
              Resources
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white leading-tight">
              Learn from the experts
            </h1>
            <p className="mt-6 text-xl text-slate-600 dark:text-slate-400">
              Guides, case studies, webinars, templates, and courses to help you master interactive advertising.
            </p>
          </div>
        </div>
      </section>

      {/* Resource Sections */}
      <section className="py-16 border-b border-slate-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            {resourceSections.map((section) => {
              const Icon = section.icon;
              const CardContent = (
                <>
                  <div className="flex items-start justify-between">
                    <div className={`w-14 h-14 bg-gradient-to-br ${section.color} rounded-xl flex items-center justify-center`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="px-3 py-1 text-sm font-medium text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-white/5 rounded-full">
                      {section.stats}
                    </span>
                  </div>

                  <h3 className="mt-6 text-2xl font-semibold text-slate-900 dark:text-white group-hover:text-[#635bff] transition-colors">
                    {section.title}
                  </h3>
                  <p className="mt-3 text-slate-600 dark:text-slate-400">{section.description}</p>

                  <div className="mt-6 flex items-center text-[#635bff] font-medium">
                    Explore
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-2 transition-transform" />
                  </div>
                </>
              );

              if (section.external) {
                return (
                  <a
                    key={section.id}
                    href={section.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group p-8 bg-slate-50 dark:bg-white/5 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl hover:bg-slate-100 dark:hover:bg-white/10 hover:border-[#635bff]/30 transition-all"
                  >
                    {CardContent}
                  </a>
                );
              }

              return (
                <Link
                  key={section.id}
                  to={section.href}
                  className="group p-8 bg-slate-50 dark:bg-white/5 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl hover:bg-slate-100 dark:hover:bg-white/10 hover:border-[#635bff]/30 transition-all"
                >
                  {CardContent}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Blog</h2>
            <Link
              to="/blog"
              className="flex items-center text-[#635bff] font-medium hover:text-[#5449e6] transition-colors"
            >
              View all posts
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>

          {blogLoading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 className="w-8 h-8 text-[#635bff] animate-spin" />
              <span className="ml-3 text-slate-600 dark:text-slate-400">Loading blog posts...</span>
            </div>
          ) : blogError ? (
            <div className="text-center py-32">
              <p className="text-red-500 mb-4">{blogError}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-[#635bff] text-white rounded-lg hover:bg-[#635bff]/90 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* Blog Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedPosts.map((post, idx) => (
                  <Link
                    to={`/blog/${post.slug}`}
                    key={post.id}
                    className="group cursor-pointer bg-slate-50 dark:bg-white/5 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden hover:bg-slate-100 dark:hover:bg-white/10 hover:border-[#635bff]/30 transition-all"
                  >
                    <div className="aspect-[16/9] relative overflow-hidden">
                      {post.featured_image ? (
                        <img
                          src={post.featured_image}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className={`w-full h-full bg-gradient-to-br ${getPostGradient(idx)} flex items-center justify-center p-4`}>
                          <span className="text-white/90 text-sm font-semibold text-center line-clamp-3">{post.title}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-white group-hover:text-[#635bff] line-clamp-2 transition-colors">
                        {post.title}
                      </h3>
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                        {post.excerpt.replace(/&hellip;/g, '...').replace(/<[^>]+>/g, '').slice(0, 120)}
                      </p>
                      <p className="mt-3 text-xs text-slate-500">
                        {formatDate(post.publish_date)}{post.content ? ` | ${estimateReadTime(post.content)}` : ''}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-12 flex items-center justify-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === i + 1
                          ? 'bg-[#635bff] text-white'
                          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Resources;
